import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

const FORM_URL = 'https://readdy.ai/api/form/da6usv6ij9sffln41m60';

export default function ContactPage() {
  const { t, i18n } = useTranslation();
  const { lang = 'en' } = useParams<{ lang: string }>();
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang, i18n]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Anti-spam honeypot: silently pretend success for bots
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('sent');
      return;
    }
    formData.delete('website_alt');

    setFormStatus('sending');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('sent');
        form.reset();
      } else {
        const msg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          'Something went wrong. Please try again later.';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('Network error. Please check your connection and try again.');
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <div className="pt-20 md:pt-24 pb-4 bg-white border-b border-background-200/70">
        <div className="container-main">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <a href={`/${lang}/`} className="hover:text-primary-600 transition-colors">{t('breadcrumb.home')}</a>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-700">Contact Us</span>
          </nav>
        </div>
      </div>

      <section className="bg-white py-16 md:py-20">
        <div className="container-main text-center">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
            Contact Us
          </h1>
          <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed text-base md:text-lg">
            Get in touch with the AllChinaBuy Spreadsheet Resource Hub team for questions, feedback, suggestions, or content corrections. We are here to help.
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">How to Reach Us</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 mb-1">Email</h3>
                    <p className="text-foreground-600 text-sm">For general inquiries, feedback, and content corrections.</p>
                    <p className="text-primary-600 text-sm font-medium mt-1">contact@allchinabuyspread.cyou</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-secondary-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-question-answer-line text-secondary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 mb-1">FAQ</h3>
                    <p className="text-foreground-600 text-sm">For common questions, visit our FAQ page first.</p>
                    <a href={`/${lang}/faq/`} className="text-primary-600 text-sm font-medium mt-1 inline-block hover:underline">Visit FAQ</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-accent-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 mb-1">Order Support</h3>
                    <p className="text-foreground-600 text-sm">For order-specific issues, contact AllChinaBuy directly.</p>
                    <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-primary-600 text-sm font-medium mt-1 inline-block hover:underline">Visit AllChinaBuy</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 mb-1">Response Time</h3>
                    <p className="text-foreground-600 text-sm">We typically respond within 24-48 hours during business days.</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-yellow-50 rounded-xl border border-yellow-100">
                <p className="text-yellow-800 text-sm leading-relaxed">
                  <i className="ri-error-warning-line mr-1"></i>
                  <strong>Important:</strong> We do not process orders or handle refunds. For order-specific issues, please contact AllChinaBuy directly through their official support channels.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-background-200/70">
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">Send a Message</h3>
              {formStatus === 'sent' ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-2xl text-green-600"></i>
                  </div>
                  <h4 className="font-heading text-lg font-bold text-foreground-900 mb-2">Message Sent!</h4>
                  <p className="text-foreground-600 text-sm">Thank you for reaching out. We will get back to you as soon as possible.</p>
                </div>
              ) : (
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">Name</label>
                    <input id="contact-name" name="name" type="text" required className="w-full px-4 py-2.5 bg-background-50 border border-background-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 text-sm" placeholder="Your name" />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">Email</label>
                    <input id="contact-email" name="email" type="email" required className="w-full px-4 py-2.5 bg-background-50 border border-background-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 text-sm" placeholder="your@email.com" />
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">Subject</label>
                    <select id="contact-subject" name="subject" className="w-full px-4 py-2.5 bg-background-50 border border-background-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 text-sm">
                      <option>General Question</option>
                      <option>Content Correction</option>
                      <option>Feature Request</option>
                      <option>Report a Problem</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">Message</label>
                    <textarea id="contact-message" name="message" required rows={4} maxLength={500} className="w-full px-4 py-2.5 bg-background-50 border border-background-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 text-sm resize-none" placeholder="How can we help you?"></textarea>
                    <p className="text-xs text-foreground-400 mt-1">Maximum 500 characters</p>
                  </div>

                  {/* Anti-spam honeypot */}
                  <input
                    type="text"
                    name="website_alt"
                    className="form-aux"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {formStatus === 'error' && formError && (
                    <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-red-700 text-sm leading-relaxed">
                      <i className="ri-error-warning-line mr-1"></i>
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'sending'}
                    className="w-full btn-primary justify-center disabled:opacity-50"
                  >
                    {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
                    <i className="ri-send-plane-line ml-2"></i>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}